import { useRef, useState } from 'react'
import { TbCheck } from 'react-icons/tb'
import { Heading } from 'ui-kit/typography'
import { IntegrationCollectionType } from 'routes/Integrations/helpers/types'
import { IntegrationMissingData } from 'routes/Integrations/Integration/components/IntegrationMissingData'
import { IntegrationLoadMoreButton } from 'routes/Integrations/Integration/components/IntegrationLoadMoreButton'
import { collectionOperations } from 'routes/Integrations/helpers/collectionOperations'
import clsx from 'clsx'
import classes from './IntegrationPageDataCollections.module.scss'

const INITIAL_COLLECTIONS_COUNT = 5

export function IntegrationPageDataCollections({
  dataCollections,
}: {
  dataCollections: IntegrationCollectionType[]
}) {
  if (!dataCollections?.length) {
    return null
  }

  const [seeMore, setSeeMore] = useState(false)
  const elementToScroll = useRef<HTMLElement>(null)

  const toggleSeeMoreBtn = () => setSeeMore(!seeMore)

  const isMoreDataCollectionsAvailable =
    dataCollections.length > INITIAL_COLLECTIONS_COUNT

  const numberOfAdditionalDataCollections =
    dataCollections.length - INITIAL_COLLECTIONS_COUNT

  const operationsToShow = collectionOperations.filter((o) =>
    dataCollections.find((c) => o.check(c)),
  )

  const sortedDataCollections = dataCollections.map((collection) => ({
    ...collection,
    supportedOps: operationsToShow.filter((o) => o.check(collection)).length,
  }))

  sortedDataCollections.sort((a, b) => {
    if (a.supportedOps === b.supportedOps) return 0
    if (a.supportedOps === 0) return 1
    if (b.supportedOps === 0) return -1
    return a.supportedOps < b.supportedOps ? 1 : -1
  })

  const visibleDataCollections = seeMore
    ? sortedDataCollections
    : sortedDataCollections.slice(0, INITIAL_COLLECTIONS_COUNT)

  return (
    <section className={classes.section} ref={elementToScroll}>
      <div className={classes.wrapper}>
        <Heading as={'h2'} size={'xl'} center>
          Data Collections
        </Heading>
        <div className={classes.content}>
          <div className={classes.table_wrap}>
            <table className={classes.table}>
              <thead className={classes.table_header}>
                <tr>
                  <th className={classes.table_header__col} />
                  {operationsToShow.map((operation) => (
                    <th key={operation.key}>{operation.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleDataCollections.map((collection) => (
                  <tr key={collection.key}>
                    <td>{collection.name}</td>
                    {operationsToShow.map((operation) => (
                      <td key={operation.key}>
                        {operation.check(collection) ? (
                          <CheckedItem checked />
                        ) : (
                          <CheckedItem />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isMoreDataCollectionsAvailable && (
            <IntegrationLoadMoreButton
              toggleButton={toggleSeeMoreBtn}
              isActive={seeMore}
              scrollAnchor={elementToScroll.current as HTMLElement}
              additionalItemsAmount={numberOfAdditionalDataCollections}
              className={classes.button}
            />
          )}

          <IntegrationMissingData
            text={'Missing collections or operations?'}
            linkText={'You can add them using'}
            className={classes.cta_wrap}
          />
        </div>
      </div>
    </section>
  )
}

function CheckedItem({ checked }: { checked?: boolean }) {
  return (
    <>
      {checked ? (
        <div className={clsx(classes.round, classes.round__filled)}>
          <TbCheck />
        </div>
      ) : (
        <div className={clsx(classes.round, classes.round__empty)}></div>
      )}
    </>
  )
}
